import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';

interface ApiKeyInfo {
  hasKey: boolean;
  createdAt: string | null;
  keyPreview: string | null;
}

interface ApiKeyResponse {
  apiKey: string;
  createdAt: string;
}

export default function Settings(): ReactElement {
  const qc = useQueryClient();
  const [showKeyModal, setShowKeyModal] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const { data: keyInfo } = useQuery({
    queryKey: ['api-key-info'],
    queryFn: () => api<ApiKeyInfo>('/me/api-key'),
  });

  const generateMutation = useMutation({
    mutationFn: () => api<ApiKeyResponse>('/me/api-key', { method: 'POST' }),
    onSuccess: (data) => {
      setShowKeyModal(data.apiKey);
      qc.invalidateQueries({ queryKey: ['api-key-info'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => api('/me/api-key', { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-key-info'] });
      setConfirmRevoke(false);
    },
  });

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback: select-and-prompt
    }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-xs text-ink-subtle tracking-[0.18em] uppercase mb-2">
            Configuración
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-ink-muted mt-2">
            Acceso al sync agent, preferencias y datos de tu cuenta.
          </p>
        </div>

        {/* Sync Agent — API key */}
        <section className="rounded-xl border border-surface-border bg-surface-elevated p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-brand-500">📡</span> Sync agent
              </h2>
              <p className="text-sm text-ink-muted mt-1">
                Conectá el agente Python que corre en tu PC para auto-importar trades de MT5.
              </p>
            </div>
            {keyInfo?.hasKey ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={generateMutation.isPending}
                  onClick={() => generateMutation.mutate()}
                >
                  Regenerar
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmRevoke(true)}
                >
                  Revocar
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                loading={generateMutation.isPending}
                onClick={() => generateMutation.mutate()}
              >
                Generar API key
              </Button>
            )}
          </div>

          {keyInfo?.hasKey ? (
            <div className="rounded-lg border border-surface-border bg-surface-panel p-4 font-mono text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle font-semibold mb-1">
                    API key activa
                  </div>
                  <div className="text-ink truncate">{keyInfo.keyPreview}</div>
                </div>
                {keyInfo.createdAt && (
                  <div className="text-[11px] text-ink-faint shrink-0 hidden sm:block">
                    creada {new Date(keyInfo.createdAt).toLocaleDateString('es-CL')}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-ink-faint mt-3 leading-relaxed">
                ⚠ La key real se mostró solo una vez al generarla. Si la perdiste,
                <strong className="text-ink-muted"> Regenerar</strong> crea una nueva (y
                la actual deja de funcionar). El agente con la key vieja queda offline
                hasta actualizar su <code>.env</code>.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-surface-border bg-surface-panel/30 p-6 text-center">
              <p className="text-sm text-ink-muted mb-4">
                No tenés API key generada. Generá una para autenticar el agente Python.
              </p>
            </div>
          )}

          {/* Setup instructions */}
          <details className="mt-6 group">
            <summary className="cursor-pointer text-sm font-semibold text-ink hover:text-brand-500 transition-colors flex items-center gap-2">
              <span className="group-open:rotate-90 transition-transform">▸</span>
              Cómo instalar el agente en tu PC
            </summary>
            <div className="mt-4 space-y-4 text-sm text-ink-muted">
              <Step n={1} title="Generá tu API key (botón arriba)">
                La copiás apenas se muestra. Sólo se ve una vez.
              </Step>
              <Step n={2} title="Identificá tu accountId">
                Andá a <a href="/accounts" className="text-brand-500 hover:text-brand-400">/accounts</a>, click en
                la cuenta. La URL queda como <code className="text-xs bg-surface-panel px-1 rounded">/calendar?accountId=cmoxr...</code>.
                Copiá ese id.
              </Step>
              <Step n={3} title="Instalá Python en tu PC">
                Descargá <a href="https://www.python.org/downloads/" className="text-brand-500 hover:text-brand-400" target="_blank" rel="noreferrer">python.org</a>.
                Marcá "Add Python to PATH" durante la instalación.
              </Step>
              <Step n={4} title="Bajá el agente">
                Carpeta <code className="text-xs bg-surface-panel px-1 rounded">apps/agent/</code> del repo
                (o pídeselo a tu admin). Guardalo en <code className="text-xs bg-surface-panel px-1 rounded">C:\bini-trading-agent\</code>.
              </Step>
              <Step n={5} title="Instalá deps">
                <pre className="mt-1 p-2 bg-surface-base rounded text-xs font-mono">
                  cd C:\bini-trading-agent
                  {'\n'}pip install -r requirements.txt
                </pre>
              </Step>
              <Step n={6} title="Configurá .env">
                Copiá <code className="text-xs">.env.example</code> a <code className="text-xs">.env</code>,
                pegá tu API key y accountId.
              </Step>
              <Step n={7} title="Abrí MT5 con tu cuenta logueada">
                MT5 tiene que estar corriendo y conectado al broker mientras el agente sincroniza.
              </Step>
              <Step n={8} title="Probá un sync">
                <pre className="mt-1 p-2 bg-surface-base rounded text-xs font-mono">
                  python bini_agent.py --once
                </pre>
                Si todo va bien, vas a ver tus trades de MT5 en <a href="/calendar" className="text-brand-500 hover:text-brand-400">/calendar</a> con badge MT5.
              </Step>
              <Step n={9} title="Modo continuo (opcional)">
                <pre className="mt-1 p-2 bg-surface-base rounded text-xs font-mono">
                  python bini_agent.py
                </pre>
                Sincroniza cada 30s mientras la terminal esté abierta.
              </Step>
            </div>
          </details>
        </section>

        {/* Account info */}
        <section className="rounded-xl border border-surface-border bg-surface-elevated p-6">
          <h2 className="text-lg font-semibold mb-3">Tu cuenta</h2>
          <p className="text-sm text-ink-muted">
            Más opciones próximamente — cambiar password, exportar datos, eliminar cuenta.
          </p>
        </section>
      </div>

      {/* Modal — mostrar key recién generada */}
      <Modal
        open={!!showKeyModal}
        onClose={() => setShowKeyModal(null)}
        title="API key generada"
        description="Copiala AHORA — sólo se muestra una vez. Después solo verás el preview."
        size="md"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button
              variant="secondary"
              onClick={() => showKeyModal && copy(showKeyModal)}
            >
              📋 Copiar
            </Button>
            <Button variant="primary" onClick={() => setShowKeyModal(null)}>
              Listo, la guardé
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="p-4 rounded-lg bg-surface-panel border border-surface-border font-mono text-sm break-all">
            {showKeyModal}
          </div>
          <div className="text-xs text-warn-DEFAULT bg-warn-DEFAULT/10 border border-warn-DEFAULT/30 rounded p-3 leading-relaxed">
            ⚠️ <strong>Guardala en lugar seguro</strong>. Tratala como password — quien la
            tenga puede sincronizar trades a tu journal. Si la perdés, generá una nueva
            (la vieja queda revocada).
          </div>
        </div>
      </Modal>

      {/* Confirmar revocar */}
      <Modal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        title="Revocar API key"
        description="El agente que use esta key dejará de funcionar inmediatamente."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate()}
            >
              Sí, revocar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-muted">
          Vas a tener que generar una nueva key después y actualizar el <code>.env</code> del agente.
        </p>
      </Modal>
    </AppLayout>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-6 h-6 rounded-full bg-brand-500/15 border border-brand-500/40 text-brand-500 text-[11px] font-bold flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1">
        <div className="font-medium text-ink mb-0.5">{title}</div>
        <div className="text-[13px] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
